'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, usePathname, useRouter } from '@framework/next/navigation';
// @ts-ignore history ships no type declarations
import { History } from 'history';
import { AlertManager } from '@dealroadshow/uikit-alert';
import { useMessageManager } from '@framework/message';

interface IError {
  code: string,
  data: object,
}

interface IProps {
  children: React.ReactNode,
  history?: History,
}

const useGlobalError = (
  history?: History,
) => {
  const errorPathRef = useRef<string>(null);
  const [isGlobalErrorVisible, setIsGlobalErrorVisible] = useState<boolean>(false);
  // @ts-ignore
  const [error, setError] = useState<IError | {}>(null);
  // @ts-ignore
  const [message, setMessage] = useState<string>(null);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const currentPathname = history?.location?.pathname || pathname;

  const replaceUrl = ({ pathname, search }: { pathname: string, search?: string }): void => {
    if (history) {
      history.replace({ pathname, search });
    } else {
      const url = search ? `${ pathname }?${ search }` : pathname;
      router.replace(url);
    }
  };

  const { getMessageByErrorCode } = useMessageManager();

  useEffect(() => {
    /**
     * handle next page router pathname change
     */
    checkGlobalErrors(currentPathname);

    if (!history) {
      return undefined;
    }

    /**
     * handle react-router pathname change
     */
    const unlisten = history.listen((params: { pathname: string }) => checkGlobalErrors(params.pathname));

    return () => {
      unlisten();
    };
  }, [currentPathname]);

  const checkGlobalErrors = (currentPathname: string) => {
    if (errorPathRef.current && currentPathname !== errorPathRef.current) {
      errorPathRef.current = null;
      hideGlobalError();
    }

    // @ts-ignore
    const errorCode = +searchParams.get('error_code');

    if (!errorCode) {
      return;
    }
    const rawErrorData = searchParams.get('error_data');
    const errorData = rawErrorData ? JSON.parse(decodeURIComponent(rawErrorData)) : {};
    const showToast = searchParams.get('show_toast');
    const errorMessage = getMessageByErrorCode(errorCode, errorData);

    if (showToast) {
      AlertManager.error(errorMessage);
      return;
    }
    errorPathRef.current = currentPathname;
    showGlobalError(errorMessage, { code: errorCode, data: errorData });

    const remainingParams = new URLSearchParams(searchParams.toString());
    remainingParams.delete('error_code');
    remainingParams.delete('error_data');
    remainingParams.delete('show_toast');

    replaceUrl({ pathname, search: remainingParams?.toString() || undefined });
  };

  const showGlobalError = (message: string, error: IError | {} = {}) => {
    setError(error);
    setMessage(message);
    setIsGlobalErrorVisible(true);
  };

  const hideGlobalError = () => {
    setIsGlobalErrorVisible(false);
  };

  return {
    isGlobalErrorVisible,
    error,
    message,
    hideGlobalError,
  };
};

type GlobalErrorContextType = ReturnType<typeof useGlobalError>;

// @ts-ignore
export const GlobalErrorContext = createContext<GlobalErrorContextType>(null);

export const useGlobalErrorContext = (): GlobalErrorContextType => {
  const context = useContext<GlobalErrorContextType>(GlobalErrorContext);
  if (!context) {
    throw new Error('useGlobalErrorContext must be used within a GlobalErrorContextProvider');
  }
  return context;
};

export default ({ children, history }: IProps) => (
  <GlobalErrorContext.Provider value={ useGlobalError(history) }>
    { children }
  </GlobalErrorContext.Provider>
);
